import { useEffect, useRef, useCallback } from "react";
import { fetchAuthSession } from "aws-amplify/auth";
import type { Job } from "../lib/types";

const WS_URL = import.meta.env.VITE_WS_URL ?? "";

interface JobUpdateMessage {
  type: "JOB_UPDATE";
  job: Job;
}

interface YouTubeExtractMessage {
  type: "youtube-extract-complete";
  taskId: string;
  status: string;
  s3Key?: string;
  previewUrl?: string;
  duration?: number;
  contentType?: string;
  error?: string;
}

type WebSocketMessage = JobUpdateMessage | YouTubeExtractMessage;

interface UseWebSocketOptions {
  onJobUpdate?: (job: Job) => void;
  onMessage?: (message: any) => void;
  enabled?: boolean;
}

export function useWebSocket({ onJobUpdate, onMessage, enabled = true }: UseWebSocketOptions) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const reconnectAttempts = useRef(0);
  const onJobUpdateRef = useRef(onJobUpdate);
  const onMessageRef = useRef(onMessage);
  onJobUpdateRef.current = onJobUpdate;
  onMessageRef.current = onMessage;

  const connect = useCallback(async () => {
    if (!WS_URL || !enabled) return;

    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      if (!token) return;

      // Close existing connection
      if (wsRef.current) {
        wsRef.current.close();
      }

      const ws = new WebSocket(`${WS_URL}?token=${token}`);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("[WS] Connected");
        reconnectAttempts.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data) as WebSocketMessage;
          
          // Call generic message handler first if provided
          if (onMessageRef.current) {
            onMessageRef.current(msg);
          }
          
          // Handle specific message types
          if (msg.type === "JOB_UPDATE" && msg.job && onJobUpdateRef.current) {
            console.log("[WS] Job update:", msg.job.jobId, msg.job.status);
            onJobUpdateRef.current(msg.job);
          } else if (msg.type === "youtube-extract-complete") {
            console.log("[WS] YouTube extract update:", msg.taskId, msg.status);
          }
        } catch (err) {
          console.warn("[WS] Failed to parse message:", err);
        }
      };

      ws.onclose = (event) => {
        console.log("[WS] Disconnected:", event.code, event.reason);
        wsRef.current = null;
        // Reconnect with exponential backoff
        if (enabled) {
          const delay = Math.min(1000 * 2 ** reconnectAttempts.current, 30000);
          reconnectAttempts.current++;
          console.log(`[WS] Reconnecting in ${delay}ms...`);
          reconnectTimerRef.current = setTimeout(connect, delay);
        }
      };

      ws.onerror = (err) => {
        console.warn("[WS] Error:", err);
      };
    } catch (err) {
      console.warn("[WS] Connection failed:", err);
      // Retry
      const delay = Math.min(1000 * 2 ** reconnectAttempts.current, 30000);
      reconnectAttempts.current++;
      reconnectTimerRef.current = setTimeout(connect, delay);
    }
  }, [enabled]);

  useEffect(() => {
    connect();

    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);
}
